import { Button, openModal, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getFileDownloadUrl } from "../../api/firebase/firestoreService";
import { getAuthLetterAgentDiff } from "../../api/postCall/getAuthLetterAgentDiff";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import NarLetterModal from "../../components/modals/narLetterModal/narLetterModal";
import {
  AUTHORISATION_DETAILS_SAVED_SUCCESSFULLY,
  FAILED_TO_SAVE_AUTHORISATION_DETAILS,
} from "../../constants/stringLiterals";
import { useDataAccumulatorForSingleOrder } from "../../context/dataAccumulatorForSingleOrder";
import { DocumentTypesForOrder } from "../../enums/documentTypes";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { EventsName } from "../../enums/eventsName";
import { ModalId } from "../../enums/modalId";
import {
  getAuthLetterExtractionJCodeKey,
  getAuthLetterExtractionKey,
} from "../../redux/slice/authLetterExtractionSlice";
import { closeModal } from "../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import { CrossIcon } from "../../svg/cross-icon";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { showApiError } from "../../utils/showApiError";
import { apiWorkerManager } from "../../workerManagers/workerManager";
import { handleAuthLetterUpload } from "../worklists/singleOrder/cptCodes/utils/authLetterModalHandlers";
import AuthorisationDocumentViewer from "./components/AuthorisationDocumentViewer";
import BasicInformation from "./components/BasicInformation";
import DiagnosisDetail from "./components/DiagnosisDetail";
import ProcedureInformation from "./components/ProcedureInformation";
import {
  useAuthorisationLetterContext,
  type AuthorisationDetailsLocationState,
  type ServerHydratedRejectFlags,
} from "./hooks/useAuthorisationLetterContext";
import {
  AUTHORISATION_DETAILS_TITLE,
  CANCEL_BUTTON_TEXT,
  LOADING_AGENT_DIFF_MESSAGE,
  LOADING_AGENT_RERUN_MESSAGE,
  ProcedureFieldState,
  SAVE_BUTTON_TEXT,
  type ApprovalStatus,
  type BasicInfoFieldState,
  type DiagnosisDetailItemState,
} from "./utils/constants";
import { partitionAgentDiffsBySection } from "./utils/mapAgentDiffToUiKeys";
import { saveAuthorisationDetailsReview } from "./utils/saveAuthorisationDetailsReview";
import { validateRequiredAuthFields } from "./utils/validateRequiredAuthFields";

export type { ApprovalStatus };

/** Shown while Postgres auth-on-file data is loading for the route order. */
const AUTH_ON_FILE_LOADING_MESSAGE = "Loading authorisation data...";

const AuthorisationDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { authId } = useParams<{ authId?: string }>();
  const orderId = authId ?? "";
  const orgId = getOrgIdForFetchExternalWorklist();
  const { openedModalId } = useSelector((s: RootState) => s.modal);
  const authLetterExtractionLoading = useSelector(
    (s: RootState) => s.authLetterExtraction.loadingByDocument,
  );
  const authLetterExtractionLoadingByJCode = useSelector(
    (s: RootState) => s.authLetterExtraction.loadingByJCode,
  );
  const { user } = useSelector((s: RootState) => s.firebaseAuthentication);
  const {
    generalOrderDetails,
    fetchGeneralOrderDetails,
    fetchAuthOnFileDetails,
    authOnFileDetails,
    authOnFileDetailsLoading,
    authOnFileDetailsError,
    authorizationData,
    authorizationDataLoading,
    fetchAuthorizationData,
    fetchAll,
  } = useDataAccumulatorForSingleOrder();

  useEffect(() => {
    if (!orderId.trim() || !orgId.trim()) {
      return;
    }
    void fetchAuthOnFileDetails(orderId, orgId);
    void fetchGeneralOrderDetails(orderId, orgId);
    void fetchAuthorizationData(orderId, orgId);
  }, [
    orderId,
    orgId,
    fetchAuthOnFileDetails,
    fetchGeneralOrderDetails,
    fetchAuthorizationData,
  ]);

  const authOnFile = useMemo(
    () => authOnFileDetails?.auth_on_file ?? undefined,
    [authOnFileDetails],
  );
  const patientIdForReviewDump = useMemo(
    () =>
      generalOrderDetails?.order?.demographics?.patient_mrn ??
      generalOrderDetails?.order?.payload?.patient_id ??
      "",
    [generalOrderDetails],
  );

  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [letterTitle, setLetterTitle] = useState<string>("Letter 1.pdf");
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [basicInfoStatus, setBasicInfoStatus] = useState<ApprovalStatus>(null);
  const [diagnosisDetailStatus, setDiagnosisDetailStatus] =
    useState<ApprovalStatus>(null);
  const [procedureStatuses, setProcedureStatuses] = useState<
    Record<string, ApprovalStatus>
  >({});
  const [procedureFieldStateMap, setProcedureFieldStateMap] = useState<
    Record<string, ProcedureFieldState>
  >({});
  const [basicInfoFieldState, setBasicInfoFieldState] =
    useState<BasicInfoFieldState | null>(null);
  const [diagnosisDetailFieldState, setDiagnosisDetailFieldState] = useState<
    DiagnosisDetailItemState[] | null
  >(null);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [basicInfoHasChanges, setBasicInfoHasChanges] = useState(false);
  const [diagnosisDetailHasChanges, setDiagnosisDetailHasChanges] =
    useState(false);
  const [procedureChangesMap, setProcedureChangesMap] = useState<
    Record<string, boolean>
  >({});
  const [serverHydratedReject, setServerHydratedReject] =
    useState<ServerHydratedRejectFlags>({
      basic: false,
      diagnosis: false,
      procedures: {},
    });
  const [agentDiffBasicKeys, setAgentDiffBasicKeys] = useState<
    Set<keyof BasicInfoFieldState>
  >(() => new Set());
  const [agentDiffDiagnosisKeys, setAgentDiffDiagnosisKeys] = useState<
    Set<keyof DiagnosisDetailItemState>
  >(() => new Set());
  const [agentDiffProcedureByJCode, setAgentDiffProcedureByJCode] = useState<
    Record<string, Set<keyof ProcedureFieldState>>
  >({});
  const [agentDiffLoading, setAgentDiffLoading] = useState(false);
  /**
   * Ensures {@link applyServerHydratedRejectFlags} runs once on first meaningful
   * load; when `jCodes` later expands (navigation state → full Postgres list),
   * procedure reject hydration is merged without resetting basic/diagnosis flags.
   */
  const appliedInitialRejectFlagsRef = useRef(false);

  const {
    letterDocPath,
    letterTitle: derivedLetterTitle,
    jCodes,
    jCode,
    allPartsHaveStatus,
  } = useAuthorisationLetterContext(
    location,
    authOnFile,
    {
      basicInfoStatus,
      diagnosisDetailStatus,
      procedureStatuses,
    },
    basicInfoHasChanges,
    diagnosisDetailHasChanges,
    procedureChangesMap,
    serverHydratedReject,
  );

  /**
   * Required-field gate: Save is blocked until every required auth field
   * (auth_number, source_portal, and per-J-code num_visits / start_date /
   * end_date / auth_status) holds a valid value. The invalid-key sets also
   * drive the dynamic red asterisks on section / row headers.
   */
  const requiredFieldsCheck = useMemo(
    () =>
      validateRequiredAuthFields({
        jCodes,
        basicInfoFieldState,
        procedureFieldStateMap,
      }),
    [jCodes, basicInfoFieldState, procedureFieldStateMap],
  );

  const basicInfoRequiredInvalidKeys = useMemo(() => {
    const keys = new Set<keyof BasicInfoFieldState>();
    for (const issue of requiredFieldsCheck.issues) {
      if (issue.section === "basicInformation") {
        keys.add(issue.fieldKey as keyof BasicInfoFieldState);
      }
    }
    return keys;
  }, [requiredFieldsCheck.issues]);

  const procedureRequiredInvalidKeys = useMemo(() => {
    const map: Record<string, Set<keyof ProcedureFieldState>> = {};
    for (const issue of requiredFieldsCheck.issues) {
      if (issue.section !== "procedure" || issue.jCodeId === undefined) {
        continue;
      }
      (map[issue.jCodeId] ??= new Set()).add(
        issue.fieldKey as keyof ProcedureFieldState,
      );
    }
    return map;
  }, [requiredFieldsCheck.issues]);

  const getInitialSectionStatuses = useCallback(() => {
    const firstAuthLetter = jCode?.auth_letter;
    const basicValidated = firstAuthLetter?.basic_info_validated;
    const initialBasicStatus: ApprovalStatus =
      basicValidated === true
        ? "valid"
        : basicValidated === false
          ? "invalid"
          : null;

    const diagnosisValidated = firstAuthLetter?.diagnosis_validated;
    const initialDiagnosisStatus: ApprovalStatus =
      diagnosisValidated === true
        ? "valid"
        : diagnosisValidated === false
          ? "invalid"
          : null;

    const initialProcedureStatuses = jCodes.reduce<
      Record<string, ApprovalStatus>
    >((acc, item) => {
      const id = item.id ?? item.j_code ?? "";
      if (!id) return acc;
      /** Prefer J-code root (save merge) then nested auth letter (Postgres). */
      const humanValidated =
        item.human_validated ?? item.auth_letter?.human_validated;
      if (humanValidated === true) {
        acc[id] = "valid";
      } else if (humanValidated === false) {
        acc[id] = "invalid";
      }
      return acc;
    }, {});

    return {
      initialBasicStatus,
      initialDiagnosisStatus,
      initialProcedureStatuses,
    };
  }, [jCode, jCodes]);

  /**
   * Sets which sections still reflect a reject loaded from Postgres (vs user chip).
   * Call whenever initial statuses are re-applied (first load or Cancel).
   *
   * @param initials - Output of {@link getInitialSectionStatuses}
   */
  const applyServerHydratedRejectFlags = useCallback(
    (initials: ReturnType<typeof getInitialSectionStatuses>): void => {
      setServerHydratedReject({
        basic: initials.initialBasicStatus === "invalid",
        diagnosis: initials.initialDiagnosisStatus === "invalid",
        procedures: Object.fromEntries(
          Object.entries(initials.initialProcedureStatuses)
            .filter(([, s]) => s === "invalid")
            .map(([id]) => [id, true]),
        ),
      });
    },
    [],
  );

  const handleBasicInfoApprovalChange = useCallback(
    (status: ApprovalStatus) => {
      setBasicInfoStatus(status);
      setServerHydratedReject((prev) => ({ ...prev, basic: false }));
    },
    [],
  );

  const handleDiagnosisApprovalChange = useCallback(
    (status: ApprovalStatus) => {
      setDiagnosisDetailStatus(status);
      setServerHydratedReject((prev) => ({ ...prev, diagnosis: false }));
    },
    [],
  );

  const handleProcedureStatusChange = useCallback(
    (id: string, status: ApprovalStatus) => {
      setProcedureStatuses((prev) => ({ ...prev, [id]: status }));
      setServerHydratedReject((prev) => ({
        ...prev,
        procedures: { ...prev.procedures, [id]: false },
      }));
    },
    [],
  );

  useEffect(() => {
    appliedInitialRejectFlagsRef.current = false;
  }, [orderId]);

  useEffect(() => {
    if (!jCodes.length) return;

    const initials = getInitialSectionStatuses();

    setBasicInfoStatus((prev) =>
      prev !== null ? prev : initials.initialBasicStatus,
    );
    setDiagnosisDetailStatus((prev) =>
      prev !== null ? prev : initials.initialDiagnosisStatus,
    );

    setProcedureStatuses((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const item of jCodes) {
        const id = item.id ?? item.j_code ?? "";
        if (!id) continue;
        if (Object.prototype.hasOwnProperty.call(next, id)) continue;
        const fromServer = initials.initialProcedureStatuses[id];
        if (fromServer !== undefined) {
          next[id] = fromServer;
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    if (!appliedInitialRejectFlagsRef.current) {
      applyServerHydratedRejectFlags(initials);
      appliedInitialRejectFlagsRef.current = true;
    } else {
      setServerHydratedReject((prev) => ({
        ...prev,
        procedures: {
          ...prev.procedures,
          ...Object.fromEntries(
            Object.entries(initials.initialProcedureStatuses)
              .filter(([, st]) => st === "invalid")
              .map(([id]) => [id, true]),
          ),
        },
      }));
    }
  }, [jCodes, getInitialSectionStatuses, applyServerHydratedRejectFlags]);
  const handleProcedureFieldStateUpdate = useCallback(
    (id: string, state: ProcedureFieldState | null) => {
      setProcedureFieldStateMap((prev) => {
        if (state === null) {
          const next = { ...prev };
          delete next[id];
          return next;
        }
        return { ...prev, [id]: state };
      });
    },
    [],
  );
  // PDF: from state (passed when opening from CPT codes green icon) or from jCode auth letter
  useEffect(() => {
    let cancelled = false;
    const path = letterDocPath ?? jCode?.auth_letter?.auth_letter_path;
    setPdfUrl("");
    if (!path) {
      setPdfLoading(false);
      return;
    }
    setLetterTitle(derivedLetterTitle);
    setPdfLoading(true);
    getFileDownloadUrl(path)
      .then((url) => {
        if (!cancelled && url) setPdfUrl(url);
      })
      .catch((error) => {
        console.error("Failed to load authorisation letter PDF:", error);
        if (!cancelled) setPdfUrl("");
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [letterDocPath, jCode?.auth_letter?.auth_letter_path, derivedLetterTitle]);

  const authLetterPathForDiff =
    letterDocPath ?? jCode?.auth_letter?.auth_letter_path;
  const locationState =
    location.state as AuthorisationDetailsLocationState | null;
  const selectedJCodeId = locationState?.jCodeId ?? jCode?.id ?? jCode?.j_code;
  const isCurrentLetterExtracting = Boolean(
    authLetterPathForDiff &&
    authLetterExtractionLoading[
      getAuthLetterExtractionKey(orderId, authLetterPathForDiff)
    ],
  );
  const isSelectedJCodeExtracting = Boolean(
    selectedJCodeId &&
    authLetterExtractionLoadingByJCode[
      getAuthLetterExtractionJCodeKey(orderId, selectedJCodeId)
    ],
  );
  const isAnyJCodeExtracting = jCodes.some((item) => {
    const id = item.id ?? item.j_code;
    return Boolean(
      id &&
      authLetterExtractionLoadingByJCode[
        getAuthLetterExtractionJCodeKey(orderId, id)
      ],
    );
  });
  const isAuthLetterExtracting =
    isCurrentLetterExtracting ||
    isSelectedJCodeExtracting ||
    isAnyJCodeExtracting;

  useEffect(() => {
    let cancelled = false;
    if (!orderId?.trim() || !authLetterPathForDiff?.trim()) {
      setAgentDiffBasicKeys(new Set());
      setAgentDiffDiagnosisKeys(new Set());
      setAgentDiffProcedureByJCode({});
      setAgentDiffLoading(false);
      return;
    }

    setAgentDiffLoading(true);
    getAuthLetterAgentDiff({
      order_id: orderId,
      auth_letter_path: authLetterPathForDiff,
    })
      .then((res) => {
        if (cancelled) return;
        const rows = res?.j_code_diffs;
        if (!rows?.length) {
          setAgentDiffBasicKeys(new Set());
          setAgentDiffDiagnosisKeys(new Set());
          setAgentDiffProcedureByJCode({});
          return;
        }
        const { basicKeys, diagnosisKeys, procedureKeysByJCode } =
          partitionAgentDiffsBySection(rows);
        setAgentDiffBasicKeys(basicKeys);
        setAgentDiffDiagnosisKeys(diagnosisKeys);
        setAgentDiffProcedureByJCode(procedureKeysByJCode);
      })
      .finally(() => {
        if (!cancelled) setAgentDiffLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId, authLetterPathForDiff]);

  const handleClose = () => navigate(-1);
  const handleSave = useCallback(async () => {
    if (!requiredFieldsCheck.valid) return;
    const jCodeIds = jCodes.map((j) => j.id ?? j.j_code ?? "").filter(Boolean);
    setIsSaving(true);
    await saveAuthorisationDetailsReview({
      orderId,
      patientId: patientIdForReviewDump,
      orgId,
      authOnFile,
      jCodeIds,
      fetchAll,
      procedureFieldStateMap,
      basicInfoFieldState,
      diagnosisDetailFieldState,
      basicInfoStatus,
      diagnosisDetailStatus,
      procedureStatuses,
      primaryPlanNameFallback: jCode?.auth_letter?.primary_insurance,
      onSuccess: () => {
        toast.success(AUTHORISATION_DETAILS_SAVED_SUCCESSFULLY);
        handleClose();
      },
      onError: (error) => {
        showApiError(error, FAILED_TO_SAVE_AUTHORISATION_DETAILS);
        console.error("Failed to save authorisation details:", error);
      },
    });
    setIsSaving(false);
  }, [
    jCodes,
    orderId,
    patientIdForReviewDump,
    orgId,
    authOnFile,
    fetchAll,
    procedureFieldStateMap,
    basicInfoFieldState,
    diagnosisDetailFieldState,
    basicInfoStatus,
    diagnosisDetailStatus,
    procedureStatuses,
    jCode?.auth_letter?.primary_insurance,
    requiredFieldsCheck,
  ]);

  /**
   * Logs a BigQuery worker event, then opens the NAR letter modal for replacing an incorrect letter, with the same
   * auth-letter document dropdown as the CPT upload flow (from `authorizationData.entries`).
   */
  const handleIncorrectLetter = useCallback(() => {
    const order = generalOrderDetails?.order;
    apiWorkerManager.enqueueAndProcess({
      patient_id: order?.demographics?.patient_id ?? "",
      order_id: order?.order_id ?? orderId,
      org_id: orgId ?? "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name: EventsName.AUTHORISATION_DETAILS_INCORRECT_LETTER_CLICKED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTHORISATION_DETAILS_SCREEN,
        doc_type: DocumentTypesForOrder.AUTH_LETTER,
      },
    });
    const authStatus = authOnFile?.auth_status ?? "";
    handleAuthLetterUpload(
      dispatch,
      authStatus,
      orderId,
      letterDocPath,
      authorizationData ?? undefined,
      authorizationDataLoading,
    );
  }, [
    dispatch,
    authOnFile?.auth_status,
    generalOrderDetails?.order,
    letterDocPath,
    orderId,
    orgId,
    authorizationData,
    authorizationDataLoading,
    user?.email,
    user?.id,
  ]);

  const handleCancel = useCallback(() => {
    const initials = getInitialSectionStatuses();
    appliedInitialRejectFlagsRef.current = false;
    setBasicInfoStatus(initials.initialBasicStatus);
    setDiagnosisDetailStatus(initials.initialDiagnosisStatus);
    setProcedureStatuses(initials.initialProcedureStatuses);
    applyServerHydratedRejectFlags(initials);
    appliedInitialRejectFlagsRef.current = true;
    setProcedureFieldStateMap({});
    setBasicInfoFieldState(null);
    setDiagnosisDetailFieldState(null);
    setBasicInfoHasChanges(false);
    setDiagnosisDetailHasChanges(false);
    setProcedureChangesMap({});
    setResetTrigger((k) => k + 1);
  }, [getInitialSectionStatuses, applyServerHydratedRejectFlags]);

  useEffect(() => {
    if (openedModalId === ModalId.NAR_LETTER_MODAL) {
      openModal(ModalId.NAR_LETTER_MODAL);
    }
  }, [openedModalId]);

  return (
    <div className="flex h-screen flex-col bg-white">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-primaryGray-15 px-5 py-0">
        <h1 className="text-base font-bold leading-tight text-[#0F0F0F]">
          {AUTHORISATION_DETAILS_TITLE}
        </h1>
        <button
          type="button"
          onClick={handleClose}
          className="flex w-5 flex-shrink-0 flex-col items-start p-1 pb-0"
          aria-label="Close"
        >
          <CrossIcon width="20" height="20" />
        </button>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <AuthorisationDocumentViewer
          pdfUrl={pdfUrl}
          documentFileName={letterTitle}
          isLoading={pdfLoading}
          onIncorrectLetterClick={handleIncorrectLetter}
        />

        <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto p-6 pb-10">
          {authOnFileDetailsLoading && (
            <div
              className="absolute inset-0 z-[11] flex items-center justify-center bg-white/90"
              aria-busy="true"
              aria-live="polite"
            >
              <LoaderMessage message={AUTH_ON_FILE_LOADING_MESSAGE} />
            </div>
          )}
          {!!authOnFileDetailsError && !authOnFileDetailsLoading && (
            <div
              className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-red-600"
              role="alert"
            >
              Unable to load authorisation data for this order. Try again later.
            </div>
          )}
          {agentDiffLoading && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-white/80"
              aria-busy="true"
              aria-live="polite"
            >
              <LoaderMessage message={LOADING_AGENT_DIFF_MESSAGE} />
            </div>
          )}
          {isAuthLetterExtracting && (
            <div
              className="absolute inset-0 z-[12] flex items-center justify-center bg-white/90"
              aria-busy="true"
              aria-live="polite"
            >
              <LoaderMessage message={LOADING_AGENT_RERUN_MESSAGE} />
            </div>
          )}
          {!authOnFileDetailsLoading &&
            !authOnFileDetailsError &&
            jCodes.length > 0 &&
            jCode && (
              <div className="flex shrink-0 flex-col gap-6">
                <BasicInformation
                  jCode={jCode}
                  agentDiffChangedKeys={agentDiffBasicKeys}
                  approvalStatus={basicInfoStatus}
                  onApprovalStatusChange={handleBasicInfoApprovalChange}
                  onFieldStateUpdate={setBasicInfoFieldState}
                  onHasAnyChangeUpdate={setBasicInfoHasChanges}
                  resetTrigger={resetTrigger}
                  serverRejectHydrated={serverHydratedReject.basic}
                  requiredInvalidFieldKeys={basicInfoRequiredInvalidKeys}
                />
                <DiagnosisDetail
                  authLetterIcdCode={
                    jCode?.auth_letter?.diagnosis_code ??
                    jCode?.auth_letter?.auth_letter_icd_code
                  }
                  agentDiffChangedKeys={agentDiffDiagnosisKeys}
                  approvalStatus={diagnosisDetailStatus}
                  onApprovalStatusChange={handleDiagnosisApprovalChange}
                  onFieldStateUpdate={setDiagnosisDetailFieldState}
                  onHasAnyChangeUpdate={setDiagnosisDetailHasChanges}
                  resetTrigger={resetTrigger}
                  serverRejectHydrated={serverHydratedReject.diagnosis}
                />
                <ProcedureInformation
                  jCodes={jCodes}
                  procedureStatuses={procedureStatuses}
                  onProcedureStatusChange={handleProcedureStatusChange}
                  onProcedureFieldStateUpdate={handleProcedureFieldStateUpdate}
                  onHasAnyChangeUpdate={setProcedureChangesMap}
                  agentProcedureDiffByJCode={agentDiffProcedureByJCode}
                  resetTrigger={resetTrigger}
                  procedureServerRejectHydrated={
                    serverHydratedReject.procedures
                  }
                  procedureRequiredInvalidKeys={procedureRequiredInvalidKeys}
                />
              </div>
            )}
          {!authOnFileDetailsLoading &&
            !authOnFileDetailsError &&
            authOnFile &&
            jCodes.length === 0 && (
              <div className="flex flex-1 flex-col items-center justify-center p-6 text-center text-sm text-primaryGray-1">
                No authorisation letter data found for this order.
              </div>
            )}
        </div>
      </div>

      <footer className="flex shrink-0 justify-end border-t border-primaryGray-15 bg-white px-6 py-4">
        <div className="flex w-[60%] min-w-0 gap-3">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={handleCancel}
            className="flex-1"
            children={CANCEL_BUTTON_TEXT}
            disabled={false}
          />
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            className="flex-1"
            children={isSaving ? "Saving..." : SAVE_BUTTON_TEXT}
            disabled={
              !allPartsHaveStatus ||
              !requiredFieldsCheck.valid ||
              isSaving ||
              authOnFileDetailsLoading ||
              !!authOnFileDetailsError
            }
          />
        </div>
      </footer>

      {openedModalId === ModalId.NAR_LETTER_MODAL && (
        <NarLetterModal
          onClose={() => {
            dispatch(closeModal());
          }}
          showDragDropUpload={true}
          shouldLogEvent={false}
          onSuccess={(_, docPath, documentTitle) => {
            navigate(location.pathname, {
              replace: true,
              state: {
                ...locationState,
                letterDocPath: docPath,
                letterTitle: documentTitle,
                agentRerunLoading: true,
              },
            });
          }}
        />
      )}
    </div>
  );
};

export default AuthorisationDetails;
